import React from 'react';
import { Box, alpha, styled } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { GOLD_ACCENT } from 'src/components/buyCreditsDialog/styles/index.styles';
import {
  type ProPlanKey,
  type ProUnlocks,
  proPlan,
} from 'src/components/buyCreditsDialog/benefits';
import useAiGrantMath from 'src/helpers/hooks/useAiGrantMath';
import { GOLD_LIGHT, GOLD_TEXT, bodyText } from './sheet.styles';
import { NEED_PLAN_KEY, type ProNeed } from './needs';
import type { ProTier } from './tiers';

/**
 * The pitch's body: Free vs the Pro tier on sale, on the things candidates hit
 * a wall on -- AI credits for tailoring, hidden jobs, paid courses, and on Pro+
 * the monthly 1:1 call -- with
 * the wall they just hit first, and the rest of the plan named in one line
 * under them, so the whole pass is still sold.
 *
 * Every figure is one the product states elsewhere: the grant and what it
 * buys at today's fees, the store's live counts. Copy rules as in benefits.ts:
 * AI-credit features are never "free", one guidance call a month is, hidden
 * jobs reach the free list later rather than never, and nothing promises
 * outcomes.
 */

const ROW_KEYS = ['ai', 'hidden', 'courses', 'guidance'] as const;
type RowKey = (typeof ROW_KEYS)[number];

interface Row {
  key: RowKey;
  label: string;
  free: string;
  pro: string;
  /** Under the Pro side: what it comes to. */
  proNote?: string;
}

const Section = styled(Box)({
  margin: '14px 24px 0',
  '@media (max-width: 600px)': { margin: '12px 18px 0' },
});

const Grid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 1.2fr)',
  // On a phone each row's label takes its own line over a Free | Pro pair.
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)',
  },
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  overflow: 'hidden',
  fontSize: 13,
}));

const Cell = styled(Box, {
  shouldForwardProp: (p) => p !== 'lead' && p !== 'pro' && p !== 'head',
})<{ lead?: boolean; pro?: boolean; head?: boolean }>(
  ({ theme, lead, pro, head }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: head ? '7px 12px' : '10px 12px',
    lineHeight: 1.3,
    borderTop: head
      ? 'none'
      : `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    backgroundColor: lead
      ? alpha(GOLD_ACCENT, 0.14)
      : pro
        ? alpha(GOLD_ACCENT, 0.06)
        : 'transparent',
    ...(head && {
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    }),
    ...(pro
      ? { color: theme.palette.text.primary, fontWeight: head ? 800 : 700 }
      : { ...bodyText(theme), fontSize: 12.5 }),
  }),
);

// On a phone a row's Free and Pro cells sit under its label, as one block.
const phoneJoined = {
  borderTopStyle: { xs: 'none', sm: 'solid' },
  pt: { xs: 0.25, sm: '10px' },
} as const;

const ProLine = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 5,
});

const ProNote = styled(Box)(({ theme }) => ({
  marginTop: 2,
  paddingLeft: 22,
  fontSize: 11.5,
  fontWeight: 500,
  ...bodyText(theme),
}));

const AlsoLine = styled(Box)(({ theme }) => ({
  marginTop: 10,
  fontSize: 12.5,
  lineHeight: 1.45,
  ...bodyText(theme),
  '& b': { color: theme.palette.text.primary, fontWeight: 700 },
}));

const Check = () => (
  <CheckRoundedIcon
    sx={(theme) => ({
      fontSize: 17,
      mt: '-1px',
      color: GOLD_TEXT,
      flexShrink: 0,
      ...theme.applyStyles('dark', { color: GOLD_LIGHT }),
    })}
  />
);

const fmt = (n: number) => n.toLocaleString('en-IN');

const useRows = (
  tier: ProTier,
  counts: ProUnlocks | undefined,
  perMonth: string,
  courseName?: string,
): Row[] => {
  const grant = useAiGrantMath(tier.aiCredits);
  const hidden = counts?.early_access_jobs;
  const courses = counts?.paid_courses;
  return [
    {
      key: 'ai',
      label: 'Tailored resumes & cover letters',
      free: 'Pay AI credits each time',
      pro: `${fmt(grant.credits)} AI credits${perMonth}`,
      proNote: `About ${fmt(grant.tailors)} tailored resumes`,
    },
    {
      key: 'hidden',
      label: 'Hidden jobs',
      free: 'Hidden',
      pro: hidden ? `${fmt(hidden)} unlocked right now` : 'All unlocked',
    },
    {
      key: 'courses',
      label: 'Paid courses',
      free: 'Pay per course',
      pro: courses ? `All ${fmt(courses)} included` : 'Every one included',
      proNote: courseName ? `${courseName} among them` : undefined,
    },
    // Only Pro+ waives a call: on the other tiers it is paid as on Free.
    ...(tier.monthlyCall
      ? [
          {
            key: 'guidance' as const,
            label: '1:1 career call',
            free: 'Paid per call',
            pro: 'One free call a month',
          },
        ]
      : []),
  ];
};

interface ProCompareProps {
  /** The tier on sale: its AI credits, and whether the call is in it. */
  tier: ProTier;
  /** The need the opener led with: its row goes first. */
  lead?: ProNeed;
  counts?: ProUnlocks;
  /** " a month" while Pro renews: the grant comes once per paid cycle. */
  perMonth: string;
  /** The course in front of them, named under the courses row. */
  courseName?: string;
}

const ProCompare = ({
  tier,
  lead,
  counts,
  perMonth,
  courseName,
}: ProCompareProps) => {
  const leadKey: ProPlanKey | undefined = lead && NEED_PLAN_KEY[lead];
  const rows = useRows(tier, counts, perMonth, courseName);
  const ordered = [
    ...rows.filter((row) => row.key === leadKey),
    ...rows.filter((row) => row.key !== leadKey),
  ];
  // The rest of the plan, with no call line on a tier without one.
  const rest = proPlan(counts).filter(
    (item) =>
      !(ROW_KEYS as readonly string[]).includes(item.key) &&
      (item.key !== 'guidance' || tier.monthlyCall),
  );
  return (
    <Section>
      <Grid role="table" aria-label="Free and Pro compared">
        <Cell head sx={{ display: { xs: 'none', sm: 'flex' } }} />
        <Cell head>Free</Cell>
        <Cell head pro>
          {tier.name}
        </Cell>
        {ordered.map((row) => {
          const on = row.key === leadKey;
          return (
            <React.Fragment key={row.key}>
              <Cell
                lead={on}
                sx={{
                  color: 'text.primary',
                  fontWeight: on ? 700 : 600,
                  fontSize: 13,
                  gridColumn: { xs: '1 / -1', sm: 'auto' },
                  pb: { xs: 0.25, sm: '10px' },
                }}
              >
                {row.label}
              </Cell>
              <Cell lead={on} sx={phoneJoined}>
                {row.free}
              </Cell>
              <Cell lead={on} pro sx={phoneJoined}>
                <ProLine>
                  <Check />
                  {row.pro}
                </ProLine>
                {row.proNote && <ProNote>{row.proNote}</ProNote>}
              </Cell>
            </React.Fragment>
          );
        })}
      </Grid>
      {!!rest.length && (
        <AlsoLine>
          <b>Also in {tier.name}:</b>{' '}
          {rest.map((item) => item.short).join(' · ')}
        </AlsoLine>
      )}
    </Section>
  );
};

export default ProCompare;
